// 🔴 ETI-82 — FICHE prospect READ-ONLY pour le commercial (role régie).
//
// 🔑 Les champs viennent de la LIGNE DE LISTE déjà chargée (la liste est scopée
//    `created_by` côté serveur) — ⛔ AUCUN GET /prospects/{id} nu (refusé à la régie,
//    cloisonnement D4). Le suivi du dossier vient de GET /prospects/{id}/dossier
//    (read-only, scopé serveur). ⛔ Aucune action (ni porte, ni devis, ni facture).
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { Badge } from "../../../components/ui/badge";
import { dossierProspect } from "./api";

const STATUT_PROSPECT = {
  nouveau: "Nouveau", converti: "Converti", doublon: "Doublon", rejete: "Rejeté",
};
const STATUT_DOSSIER = {
  dossier_cree: "Dossier créé", visite_planifiee: "Visite planifiée",
  visite_faite: "Visite faite", pret_depot: "Prêt dépôt", depose: "Déposé",
  instruction: "En instruction", octroi: "Octroi accordé", travaux: "Travaux en cours",
  cloture: "Clôturé",
};

function Ligne({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1" style={{ borderBottom: "0.5px solid var(--oh-border)" }}>
      <span style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>{label}</span>
      <span style={{ color: "var(--oh-text)", fontSize: 13, textAlign: "right" }}>{value ?? "—"}</span>
    </div>
  );
}

export default function FicheProspectRegie({ open, onClose, prospect, tenantId }) {
  const [dossier, setDossier] = useState(null);
  const [loadingDossier, setLoadingDossier] = useState(false);

  useEffect(() => {
    if (!open || !prospect?.id) return;
    let actif = true;
    setLoadingDossier(true);
    setDossier(null);
    dossierProspect(tenantId, prospect.id)
      .then((d) => { if (actif) setDossier(d); })
      .catch(() => { if (actif) setDossier(null); })
      .finally(() => { if (actif) setLoadingDossier(false); });
    return () => { actif = false; };
  }, [open, prospect?.id, tenantId]);

  if (!prospect) return null;
  const data = prospect.data || {};
  const ent = data.entreprise || {};
  const rep = data.representant || {};
  const bat = data.batiment || {};
  const cee = data.cee || {};

  const dos = dossier?.dossier;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="eti82-fiche-prospect">
        <DialogHeader>
          <DialogTitle>{ent.raison_sociale || ent.siret || "Prospect"}</DialogTitle>
          <DialogDescription>Fiche en lecture seule — suivi de votre prospect.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Ligne label="SIRET" value={ent.siret || prospect.siret} />
          <Ligne label="Représentant" value={[rep.prenom, rep.nom].filter(Boolean).join(" ") || null} />
          <Ligne label="Site" value={bat.nom_etablissement} />
          <Ligne label="Adresse travaux" value={[bat.adresse_travaux, bat.cp_travaux, bat.ville_travaux].filter(Boolean).join(" ")} />
          <Ligne label="Opération" value={cee.operation_cee || cee.code_fiche} />
          <Ligne label="Reçu le" value={prospect.created_at ? new Date(prospect.created_at).toLocaleDateString("fr-FR") : null} />
          <Ligne label="Statut prospect" value={
            <Badge style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)" }}>
              {STATUT_PROSPECT[prospect.statut] || prospect.statut}
            </Badge>
          } />
        </div>

        {/* Suivi dossier (read-only) */}
        <div className="mt-2" style={{
          background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)",
          borderRadius: 8, padding: 12,
        }}>
          <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 6, color: "var(--oh-text)" }}>
            Suivi du dossier
          </div>
          {loadingDossier && <div style={{ color: "var(--oh-text-muted)", fontSize: 13 }}>Chargement…</div>}
          {!loadingDossier && !dossier?.converti && (
            <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
              Pas encore converti en dossier.
            </div>
          )}
          {!loadingDossier && dossier?.converti && !dos && (
            <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
              Converti — dossier en cours d'initialisation.
            </div>
          )}
          {!loadingDossier && dos && (
            <div className="space-y-1">
              <Ligne label="Référence" value={dos.reference} />
              <Ligne label="Avancement" value={
                <Badge style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }}>
                  {STATUT_DOSSIER[dos.pipeline_status || dos.statut] || dos.pipeline_status || dos.statut || "—"}
                </Badge>
              } />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
